// src/app/modules/user/components/patient/PatientDevicesComponent.tsx

import axios from 'axios'
import React, {useCallback, useEffect, useState} from 'react'
import {useParams} from 'react-router-dom'
import {API_ENDPOINTS} from '../../../../constants/apiConstants'
import {
  DeviceAllocatedPatient,
  DoctorAllocatedDeviceModel,
} from '../../../../entities/device.entity'
import {toast} from 'react-toastify'
import {ApiResponseModel, PatientModel} from '../../../../entities/entities'
import {useReduxAuth} from '../../../auth'
import AssignDevicePatientModal from '../modals/AssignDevicePatientModal'
import {formatUtcDateToUserTimezone} from '../../helper/helperFunction'
import PrimaryButton from '../common/buttons/PrimaryButton'
// Redux imports for refreshing patient data after device assignment
import { useDispatch } from 'react-redux'
import { AppDispatch } from '../../../../../store/store'
import { refreshPatientAfterDeviceChange, fetchPatientDetails } from '../../../../../store/slices/patientSlice'
import { USER_ROLES } from '../../../../constants/StatusConstants'
import { useMADoctorDropdown } from '../../context/MADoctorDropdownContext'

interface PatientDevicesComponentProps {
  patientData: PatientModel | null
}

const PatientDevicesComponent: React.FC<PatientDevicesComponentProps> = ({patientData}) => {
  const {patientId} = useParams<{patientId: string}>()
  const {currentUser} = useReduxAuth()
  const dispatch = useDispatch<AppDispatch>()
  const roleId = currentUser?.role_id
  
  // MA Doctor Dropdown
  const maDropdown = useMADoctorDropdown()
  const maSelectedDoctorId = roleId === USER_ROLES.MEDICAL_ASSISTANT ? maDropdown.selectedDoctorId : undefined

  const [assignedDevices, setAssignedDevices] = useState<DeviceAllocatedPatient[]>([])
  const [availableDevices, setAvailableDevices] = useState<any[]>([])
  const [isAssignDeviceModalOpen, setIsAssignDeviceModalOpen] = useState(false)
  const [unassigningIds, setUnassigningIds] = useState<number[]>([]) // track loading

  // whether assigning is allowed
  const canAssign = patientData?.status === 1
  const alreadyAssigned = assignedDevices.length > 0

  const fetchAllAssignedDevices = useCallback(async () => {
    if (!patientId) return
    try {
      // Build query params if MA is accessing
      const queryParams = roleId === USER_ROLES.MEDICAL_ASSISTANT && maSelectedDoctorId
        ? `doctor_id=${maSelectedDoctorId}`
        : null
      const response = await axios.get<ApiResponseModel<{devices: DeviceAllocatedPatient[]}>>(
        API_ENDPOINTS.GET_ALL_PATIENT_ASSIGNED_DEVICES(patientId, queryParams)
      )
      setAssignedDevices(response.data.data.devices || [])
    } catch (error) {
      toast.error('Failed to fetch assigned devices')
    }
  }, [patientId, roleId, maSelectedDoctorId])

  const fetchAvailableDevices = useCallback(async () => {
    try {
      // For MA, use selected doctor's ID; otherwise use current user's ID
      const doctorId = roleId === USER_ROLES.MEDICAL_ASSISTANT && maSelectedDoctorId
        ? maSelectedDoctorId
        : currentUser?.id!
      
      // Build query params - include doctor_id for MA if route ID is not a doctor ID
      let queryParams = currentUser?.isNurse ? 'isNurse=1' : ''
      if (roleId === USER_ROLES.MEDICAL_ASSISTANT && maSelectedDoctorId) {
        queryParams = queryParams ? `${queryParams}&doctor_id=${maSelectedDoctorId}` : `doctor_id=${maSelectedDoctorId}`
      }
      
      const response = await axios.get<ApiResponseModel<{devices: any[]}>>(
        API_ENDPOINTS.GET_ALL_DOCTOR_AVAILABLE_DEVICES(doctorId, queryParams || null)
      )
      setAvailableDevices(response.data.data.devices || [])
    } catch (error) {
      toast.error('Failed to fetch available devices')
    }
  }, [currentUser, roleId, maSelectedDoctorId])

  useEffect(() => {
    fetchAllAssignedDevices()
    fetchAvailableDevices()
  }, [patientId, currentUser])

  const handleUnassignDevice = async (deviceAllocatedId: number) => {
    // mark this ID as loading
    setUnassigningIds((prev) => [...prev, deviceAllocatedId])
    try {
      const response = await axios.put(API_ENDPOINTS.UNASSIGN_PATIENT_DEVICE(deviceAllocatedId), {})
      // remove from assigned list
      setAssignedDevices((prev) => prev.filter((device) => device.id !== deviceAllocatedId))
      toast.success(response.data.message)
      // refresh available list
      await fetchAvailableDevices()
      
      // 🔥 Refresh Redux patient state after device unassignment
      if (patientId) {
        const doctorId = roleId === USER_ROLES.MEDICAL_ASSISTANT ? maSelectedDoctorId : undefined
        dispatch(refreshPatientAfterDeviceChange({ patientUuid: patientId, doctorId }))
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to unassign device')
    } finally {
      // clear loading state
      setUnassigningIds((prev) => prev.filter((id) => id !== deviceAllocatedId))
    }
  }

  const handleAssignDevice = async (values: DoctorAllocatedDeviceModel, resetForm: () => void) => {
    if (!patientId) return
    try {
      const assignBody = {
        device_id: values.device_id,
        doctor_allocated_device_id: values.id,
      }
      const response = await axios.post<ApiResponseModel<any>>(
        API_ENDPOINTS.ASSIGN_PATIENT_DEVICE(patientId),
        assignBody
      )
      toast.success(response.data.message)
      resetForm()
      setIsAssignDeviceModalOpen(false)
      await fetchAllAssignedDevices()
      await fetchAvailableDevices()
      
      // 🔥 Refresh Redux patient state to update billing cycles in PatientDetailBadges
      const doctorId = roleId === USER_ROLES.MEDICAL_ASSISTANT ? maSelectedDoctorId : undefined
      dispatch(refreshPatientAfterDeviceChange({ patientUuid: patientId, doctorId }))
      dispatch(fetchPatientDetails({ patientUuid: patientId, doctorId }));
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'An error occurred while assigning the device.')
    }
  }

  return (
    <>
      <div className='card mb-5 mb-xl-10' id='kt_profile_details_view'>
        <div className='card-header d-flex justify-content-between align-items-center'>
          <h3 className='fw-bolder m-0'>Assigned Device</h3>
          {!alreadyAssigned && (
            <PrimaryButton onClick={() => setIsAssignDeviceModalOpen(true)} disabled={!canAssign}>
              + Assign Device
            </PrimaryButton>
          )}
        </div>

        {!canAssign && (
          <div className='card-body'>
            <div className='alert alert-warning mb-0'>
              This patient is inactive. You cannot assign devices until the patient is activated.
            </div>
          </div>
        )}

        <div className='card-body'>
          {assignedDevices.length ? (
            assignedDevices.map((device) => {
              const isUnassigning = unassigningIds.includes(device.id)
              return (
                <div key={device.id} className='mb-4'>
                  <div className='card border-1 shadow-sm rounded-3 p-3'>
                    <div className='d-flex justify-content-between align-items-end'>
                      <div>
                        <h4 className='fw-bold mb-1'>{device.Device?.name}</h4>
                        <p className='mb-1 text-muted'>{device.Device?.DeviceType?.name}</p>
                        <p className='mb-1'>
                          <strong>IMEI:</strong> {device.Device?.imei}
                        </p>
                        <small className='text-muted'>
                          {/* getting only date */}
                          <strong>Assigned on:</strong> {formatUtcDateToUserTimezone(device.devices_assigned_date, 'MM/DD/YYYY')}
                        </small>
                      </div>
                      <PrimaryButton
                        onClick={() => handleUnassignDevice(device.id)}
                        disabled={isUnassigning}
                      >
                        {isUnassigning ? 'Unassigning…' : 'Unassign'}
                      </PrimaryButton>
                    </div>
                  </div>
                </div>
              )
            })
          ) : (
            <div className='text-center text-muted'>No devices assigned</div>
          )}
        </div>
      </div>
      {canAssign && (
        <AssignDevicePatientModal
          isOpen={isAssignDeviceModalOpen}
          onClose={() => setIsAssignDeviceModalOpen(false)}
          handleAssign={handleAssignDevice}
          devicesList={availableDevices}
        />
      )}
    </>
  )
}

export default PatientDevicesComponent
